import {useCallback} from 'react';
import * as Sentry from '@sentry/react';
import {useLivenessModelsStore} from '../store';

type ModelConfig = {
  runtime: 'mediapipe';
  solutionPath: string;
  refineLandmarks: boolean;
};

const MEDIAPIPE_CDN_PATH = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh';

export interface UsePreloadLivenessModelsProps {
  shouldPreload?: boolean;
}

export const usePreloadLivenessModels = ({
  shouldPreload = true,
}: UsePreloadLivenessModelsProps = {}) => {
  const detector = useLivenessModelsStore(state => state.detector);
  const isLoading = useLivenessModelsStore(state => state.isLoading);
  const setDetector = useLivenessModelsStore(state => state.setDetector);
  const setIsLoading = useLivenessModelsStore(state => state.setIsLoading);

  const isLoaded = Boolean(detector);

  const preload = useCallback(async () => {
    if (!shouldPreload) {
      return;
    }

    if (isLoading || isLoaded) return;
    setIsLoading(true);

    try {
      console.log('Preloading liveness detection AI models...');

      const tf = await import('@tensorflow/tfjs');

      // Try to initialize WebGL backend first
      let backendName = 'webgl';
      let backendSupported = false;

      try {
        await tf.ready();
        backendSupported = await tf
          .setBackend('webgl')
          .then(() => true)
          .catch(() => false);

        if (backendSupported) {
          console.log('Using WebGL backend for face detection');
        }
      } catch (error) {
        Sentry.captureException(error);
        console.warn('WebGL backend failed:', error);
        backendSupported = false;
      }

      // Fallback to CPU backend if WebGL is not supported
      if (!backendSupported) {
        try {
          console.log('WebGL not supported, falling back to CPU backend...');
          backendName = 'cpu';
          await tf.setBackend('cpu');
          backendSupported = true;
          console.log('Using CPU backend for face detection');
        } catch (error) {
          Sentry.captureException(error);
          console.error('CPU backend also failed:', error);

          // Last resort: try WASM backend
          try {
            console.log('CPU backend failed, trying WASM backend...');
            backendName = 'wasm';
            await tf.setBackend('wasm');
            backendSupported = true;
            console.log('Using WASM backend for face detection');
          } catch (wasmError) {
            Sentry.captureException(error);
            console.error('All backends failed:', wasmError);
            setIsLoading(false);
            return;
          }
        }
      }

      const faceLandmarksModule = await import(
        '@tensorflow-models/face-landmarks-detection'
      );
      const {SupportedModels, createDetector} = faceLandmarksModule;

      const model = SupportedModels.MediaPipeFaceMesh;
      const modelConfig: ModelConfig = {
        runtime: 'mediapipe',
        solutionPath: MEDIAPIPE_CDN_PATH,
        refineLandmarks: true,
      };

      const livenessDetector = await createDetector(model, modelConfig);

      setDetector(livenessDetector);
      setIsLoading(false);
      console.log(
        `Liveness detection AI models preloaded successfully using ${backendName} backend`,
      );
    } catch (err) {
      Sentry.captureException(err);
      console.error('Failed to preload liveness detection AI models:', err);
      setIsLoading(false);
    }
  }, [isLoading, isLoaded, setIsLoading, setDetector, shouldPreload]);

  return {
    isLoading,
    detector,
    isLoaded,
    preload,
  };
};
